import { useState } from 'react'
import {
  Creativeservicev1Creative,
  Campaignservicev1OrderLine,
} from 'next-gen-sdk'
import { Text, Modal, Button, Fa } from '@eltoro-ui/components'
import { breakTextWithDelimiters } from 'Helpers'
import { ETIcon } from 'Assets'
import { StatsAccumulatorType } from 'types'
import { CreativeStats } from './CreativeStats'
import './CreativesDisplay.scss'

const DetailsImage = ({
  close,
  image,
  creativeStats,
}: {
  close: () => void
  image: Creativeservicev1Creative
  orderline: Campaignservicev1OrderLine
  creativeStats?: StatsAccumulatorType
}) => {
  const { files, type, name } = image
  const imageFile = files ? files[0] : undefined
  const { width, height, bucket, key } = imageFile || {}
  const url = `https://s3.amazonaws.com/${bucket}/${key}`

  if (
    creativeStats?.ctr === undefined ||
    creativeStats?.imps === undefined ||
    creativeStats?.clicks === undefined
  ) {
    return <>No stats available</>
  }
  const creativeData = {
    clicks: {
      label: 'Clicks',
      value: creativeStats.clicks || 0,
    },
    imps: {
      label: 'Imps',
      value: creativeStats.imps || 0,
    },
    CTR: {
      label: 'CTR',
      value: `${creativeStats.ctr.toFixed(2)}%`,
    },
  }

  return (
    <div className="DetailsImage">
      <div className="header-DetailsImage relative">
        <div className="text-header mb-2">
          <Text className="title-text">{name}</Text>
          <Text className="details-text">
            {(type || '').charAt(0).toUpperCase() +
              (type || '').substring(1).replace(/_/g, ' ')}
            {width && height && ` - ${width} x ${height}`}
          </Text>
        </div>
        <Button
          className="DetailsImage__close-button absolute top-0 right-0"
          onClick={close}
          iconOnly={<Fa icon="xmark" size={1} />}
        />
      </div>
      <div className="imageContainer-DetailsImage">
        {type === 'CREATIVE_TYPE_BANNER' ||
        type === 'CREATIVE_TYPE_NATIVE_BANNER' ? (
          <img
            className="imageContainer-DetailsImage--img"
            alt="thumbnail"
            src={url}
            height={height}
            width={width}
          />
        ) : type === 'CREATIVE_TYPE_OTT' || type === 'CREATIVE_TYPE_VIDEO' ? (
          <video className="img-video isPDF" src={`${url}`} controls />
        ) : (
          <ETIcon icon="campaign" className="!text-primary" />
        )}
      </div>
      {creativeStats === undefined ? null : (
        <div className="CreativeStatistics__wrap mt-2 flex flex-col gap-1">
          <span className="title-column">Creative statistics</span>
          {creativeData && <CreativeStats statsData={creativeData} />}
        </div>
      )}
    </div>
  )
}

const ImageButton = ({
  image,
  orderline,
  creativeStats,
}: {
  image: Creativeservicev1Creative
  orderline: Campaignservicev1OrderLine
  creativeStats?: StatsAccumulatorType
}) => {
  const [showDetail, setShowDetail] = useState(false)
  const { files } = image
  const imageFile = files ? files[0] : undefined
  const { width, height, bucket, key } = imageFile || {}
  const url = bucket && key ? `https://s3.amazonaws.com/${bucket}/${key}` : ''

  const NoStats =
    !creativeStats ||
    (creativeStats.ctr === undefined &&
      creativeStats.imps === undefined &&
      creativeStats.clicks === undefined)

  const random = Math.random().toString()

  return (
    <>
      <button
        type="button"
        className="ImageButton"
        disabled={NoStats}
        onClick={() => {
          if (NoStats) return
          setShowDetail(true)
        }}
      >
        <figure className="image-ImageButton !h-auto">
          {(image.type === 'CREATIVE_TYPE_BANNER' ||
            image.type === 'CREATIVE_TYPE_NATIVE_BANNER') &&
          url ? (
            <img
              className="mx-auto object-fill"
              alt="thumbnail"
              src={`${url}?random=${random}`}
              height={height}
              width={width}
            />
          ) : (image.type === 'CREATIVE_TYPE_VIDEO' ||
              image.type === 'CREATIVE_TYPE_OTT') &&
            url ? (
            <video
              className="CreativesDisplay--video img-video border-grey-100 border"
              src={`${url}?random=${random}`}
            />
          ) : (
            <ETIcon icon="campaign" className="!text-primary" />
          )}
        </figure>
        <figcaption className="title-ImageButton flex flex-col gap-1 break-words text-center">
          <span className="!text-text title-ImageButton mx-auto w-20 break-words text-center">
            {breakTextWithDelimiters(image?.name as string, 12)}
          </span>
          <span className="!text-text dimensions-ImageButton text-center">
            {width && height && `${width} x ${height}`}
          </span>
        </figcaption>
      </button>
      {showDetail ? (
        <Modal
          className="DetailsImage__modal"
          offClick={() => setShowDetail(false)}
        >
          <DetailsImage
            close={() => setShowDetail(false)}
            image={image}
            orderline={orderline}
            creativeStats={creativeStats}
          />
        </Modal>
      ) : null}
    </>
  )
}

export const CreativesDisplay = ({
  orderline,
  creatives,
  creativeStats,
}: {
  orderline: Campaignservicev1OrderLine
  creatives: Creativeservicev1Creative[]
  creativeStats: StatsAccumulatorType[]
}) => {
  const sortedCreatives = creatives.sort((a, b) => {
    const indexA = creativeStats.findIndex((stat) => stat.creativeId === a.id)
    const indexB = creativeStats.findIndex((stat) => stat.creativeId === b.id)
    return indexA - indexB
  })
  return (
    <div className="CreativesDisplay CampaignCreatives border-tint-khaki-500 isOnline border-l pl-4">
      <div className="CreativesDisplay__header header-CampaignCreatives">
        Creatives ({creatives.length})
      </div>
      <div className="CreativesDisplay__gallery gallery-CampaignCreatives gap-4">
        {sortedCreatives.length > 0 ? (
          sortedCreatives.map(
            (creative: Creativeservicev1Creative, index: number) => {
              if (!creative) return null
              return (
                <ImageButton
                  key={creative.id || 0 + index}
                  image={creative}
                  orderline={orderline}
                  creativeStats={creativeStats.find(
                    (stat) => stat.creativeId === creative.id,
                  )}
                />
              )
            },
          )
        ) : (
          <Text>No attached creatives</Text>
        )}
      </div>
    </div>
  )
}
